import '../scss/announcement.scss'
import { cmsToOccDisplayText, isNotDefaultDateOrEmpty } from '@omnia/ui/src/lib'
import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import { getBemClassBuilder } from '@omnia/ui/src/lib/lumos-plus/utils/get-bem-class-builder'
import { doesPathnameMatchAnyPageRule } from '@omnia/ui/src/lib/utils/strings/doesPathnameMatchAnyPageRule'
import type { NotificationActionButton } from '@vista-digital/occ-domain'
import { Notification } from '@vista-digital/occ-ui'
import { observer } from 'mobx-react'
import { type FC } from 'react'
import { useLocation } from 'react-router-dom-v7'
import { cmsDataService } from '@/common/services/cms.data.service'
import { navigationService } from '@/common/services/navigation.service'
import { announcementPersistentDomain, announcementSessionDomain } from '../domain/announcement-domain'
import type { AnnouncementList } from '../models/announcement.types'
import { hasAnnouncementBeenViewedWithinDays } from '../utils/has-announcement-been-viewed-within-days'

const ComponentName = 'Announcement'
const b = getBemClassBuilder(ComponentName)

export const Announcement: FC = observer(() => {
    const logger = useLogger(ComponentName)
    const pathname = useLocation().pathname

    // eslint-disable-next-line complexity -- todo
    const displayAnnouncements = cmsDataService.get().announcements?.filter(announcement => {
        if (isNotDefaultDateOrEmpty(announcement.startDate) && new Date(announcement.startDate) > new Date()) {
            return false
        }

        if (isNotDefaultDateOrEmpty(announcement.endDate) && new Date(announcement.endDate) < new Date()) {
            return false
        }

        // If displayFrequencyInDays is not set or is 0, check if announcement has been viewed during this session (session storage)
        // Else, check if the announcement has been viewed within the display frequency days (local storage)
        if (!announcement.displayFrequencyInDays) {
            if (announcementSessionDomain.data.viewed.includes(announcement.id)) {
                logger.debug('Announcement has been viewed during this session, unable to display.', {
                    announcement,
                })
                return false
            }
        } else if (hasAnnouncementBeenViewedWithinDays(announcement.id, announcement.displayFrequencyInDays)) {
            logger.debug('Announcement has been viewed within the display frequency days, unable to display.', {
                announcement,
                lastViewed: announcementPersistentDomain.data.viewed[announcement.id],
            })
            return false
        }

        if (!doesPathnameMatchAnyPageRule(pathname, announcement.pages)) {
            logger.debug('Announcement page rules do not match current page', { announcement, pathname })
            return false
        }

        logger.debug(
            'Announcement matches at least one page rule and has not been viewed within the display frequency, displaying.',
            {
                announcement,
                pathname,
            }
        )
        return true
    })

    logger.trace('Rendering', { pathname, displayAnnouncements })

    if (!displayAnnouncements?.length) {
        return null
    }

    return (
        <div className={b.block()}>
            {displayAnnouncements
                ?.slice(0, 2) // Control the number of announcements displayed at the same time
                .map(announcement => (
                    <DisplayAnnouncement key={announcement.id} {...announcement} />
                ))}
        </div>
    )
})

const DisplayAnnouncement: FC<AnnouncementList[number]> = announcement => {
    const logger = useLogger(ComponentName)

    const onClose = () => {
        logger.info('Closing announcement', { announcement })

        // Add announcement id to both session and persistent storage in case the display frequency
        // is updated in the CMS after a user has initially viewed this announcement.
        announcementSessionDomain.data.viewed = [...announcementSessionDomain.data.viewed, announcement.id]
        announcementPersistentDomain.data.viewed[announcement.id] = new Date()
    }

    const openAnnouncementUrl = (url: string) => {
        if (!url) return

        // Check if URL is absolute (http/https) or points to a media/file path
        const isAbsoluteUrl = /^https?:\/\//i.test(url)
        const isMediaFile =
            url.startsWith('/media/') || /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|png|jpg|jpeg|webp|gif)(\/)?$/i.test(url)

        // External or media, open in new tab
        if (isAbsoluteUrl || isMediaFile) {
            window.open(url, '_blank', 'noopener,noreferrer')
            return
        }

        // Otherwise treat as internal route
        navigationService.goTo(url)
    }

    const actionButton: NotificationActionButton = announcement.actionButton
        ? {
              show: true,
              label: announcement.actionButton.name,
              onClick: () => {
                  openAnnouncementUrl(announcement.actionButton!.url)
                  onClose()
              },
          }
        : {
              show: false,
          }

    return (
        <Notification
            id={`announcement-${announcement.id}`}
            wrapperClassName={b.element('item')}
            type={announcement.style}
            displayInline={true}
            title={announcement.title}
            messages={[cmsToOccDisplayText(announcement.message)]}
            actionButton={actionButton}
            onCloseButtonClick={onClose}
            closeButton={{
                show: true,
            }}
        />
    )
}

Announcement.displayName = ComponentName
